/**
 * Return Reports Types
 *
 * TypeScript interfaces for booking return reports, item returns, and photos.
 */

// Item-level status values
export type ItemStatus = 'ok' | 'issue' | 'damaged' | 'missing';

// Overall return status (derived)
export type OverallStatus = 'all_ok' | 'issues_reported';

// Review status for admin tracking
export type ReviewStatus = 'pending' | 'reviewed';

/**
 * Main booking return record
 */
export interface BookingReturn {
  id: string;
  booking_id: string;
  merchant_id: string;
  location_id: string;
  completed_sections: string[];
  booking_level_answers: Record<string, string>;
  overall_status: OverallStatus;
  description: string | null;
  submission_id: string;
  version: number;
  replaced_by: string | null;
  is_latest: boolean;
  created_at: string;
  created_at_vienna: string;
  // Admin fields
  review_status?: ReviewStatus;
  reviewed_at?: string | null;
  reviewed_by?: string | null;
  internal_notes?: string | null;
}

/**
 * Item-level return data
 */
export interface BookingItemReturn {
  id: string;
  booking_return_id: string;
  booking_id: string;
  booking_item_id: string;
  inventory_item_id: string | null;
  item_level_answers: Record<string, string>;
  item_status: ItemStatus;
  description: string | null;
  created_at: string;
  // Joined data
  booking_items?: {
    item_name: string;
    product_type: string;
    size: string | null;
    code: string | null;
    quantity: number;
  };
  inventory_items?: {
    item_number: string;
    combination_code: string | null;
    product_type_id: string;
  } | null;
}

/**
 * Photo record
 */
export interface BookingReturnPhoto {
  id: string;
  booking_return_id: string;
  booking_id: string;
  merchant_id: string;
  location_id: string;
  booking_item_return_id: string | null;
  file_name: string;
  file_path: string;
  replaced_at: string | null;
  created_at: string;
  // Computed
  public_url?: string;
}

/**
 * Return report with all related data (for detail view)
 */
export interface BookingReturnWithDetails extends BookingReturn {
  bookings: {
    order_id: string;
    customer_firstname: string;
    customer_lastname: string;
    customer_email: string;
    customer_phone: string | null;
    booking_state: string;
    start_date: string;
    end_date: string;
    returned_at: string | null;
  };
  merchants: {
    business_name: string;
    display_name: string;
  };
  locations: {
    location_name: string;
    google_maps_url?: string | null;
  };
  item_returns?: BookingItemReturn[];
  booking_photos?: BookingReturnPhoto[];
  item_photos?: BookingReturnPhoto[];
}

/**
 * List view item (from v_latest_booking_returns view)
 */
export interface ReturnReportListItem {
  return_id: string;
  booking_id: string;
  order_id: string;
  booking_state: string;
  merchant_id: string;
  merchant_name: string;
  location_id: string;
  location_name: string;
  overall_status: OverallStatus;
  completed_sections: string[];
  booking_level_answers: Record<string, string>;
  description: string | null;
  created_at: string;
  created_at_vienna: string;
  returned_at: string | null;
  customer_firstname: string;
  customer_lastname: string;
  customer_email: string;
  booking_photo_count: number;
  item_count: number;
  first_photo_url?: string | null;
  // Admin fields
  review_status?: ReviewStatus;
  internal_notes?: string | null;
}

/**
 * Version history item
 */
export interface ReturnVersionHistory {
  id: string;
  version: number;
  created_at: string;
  created_at_vienna: string;
  overall_status: OverallStatus;
  is_latest: boolean;
}

/**
 * Filter options for return reports list
 */
export interface ReturnReportFilters {
  status?: OverallStatus | 'all';
  reviewStatus?: ReviewStatus | 'all';
  locationId?: string;
  dateFrom?: string;
  dateTo?: string;
  search?: string;
}

/**
 * Status badge configuration
 */
export const STATUS_CONFIG: Record<OverallStatus, { labelKey: string; color: string; bgColor: string }> = {
  all_ok: {
    labelKey: 'returnReports.allOk',
    color: 'text-success',
    bgColor: 'bg-success/10',
  },
  issues_reported: {
    labelKey: 'returnReports.issuesReported',
    color: 'text-danger',
    bgColor: 'bg-danger/10',
  },
};

export const ITEM_STATUS_CONFIG: Record<ItemStatus, { labelKey: string; color: string; bgColor: string }> = {
  ok: {
    labelKey: 'returnReports.itemOk',
    color: 'text-success',
    bgColor: 'bg-success/10',
  },
  issue: {
    labelKey: 'returnReports.itemIssue',
    color: 'text-warning',
    bgColor: 'bg-warning/10',
  },
  damaged: {
    labelKey: 'returnReports.itemDamaged',
    color: 'text-danger',
    bgColor: 'bg-danger/10',
  },
  missing: {
    labelKey: 'returnReports.itemMissing',
    color: 'text-danger',
    bgColor: 'bg-danger/10',
  },
};

export const REVIEW_STATUS_CONFIG: Record<ReviewStatus, { labelKey: string; color: string; bgColor: string }> = {
  pending: {
    labelKey: 'returnReports.pendingReview',
    color: 'text-warning',
    bgColor: 'bg-warning/10',
  },
  reviewed: {
    labelKey: 'returnReports.reviewed',
    color: 'text-success',
    bgColor: 'bg-success/10',
  },
};
